import {
  Pagination as MuiPagination,
  TablePagination as MuiTablePagination,
  PaginationItem,
  type PaginationProps,
  type TablePaginationProps,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useThemeTokens } from '@providers/themeTokenProvider';
import type { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '../menuItem/menuItem';
import { SelectField } from '../selectField/selectField';
import { usePaginationTokens } from './pagination.tokens';
import { usePaginationSx } from './usePaginationSx';

export type PaginationVariant = 'default' | 'tableFooter';

interface IPaginationProps extends PaginationProps {
  perPage?: number;
  total?: number;
  onChangePerPage?: (perPage: number) => void;
  showPerPage?: boolean;
  pageSizes?: number[];
  /** i18n key for the “start–end of total” label (default: global lists/tables). */
  summaryKey?: string;
  /** i18n namespace for {@link summaryKey} (default `shared`). */
  summaryNamespace?: string;
  /**
   * `default`: legacy layout (standalone pages and most {@link DataTable} footers).
   * `tableFooter`: full-width bar with first/last buttons — opt in (e.g. recon flags).
   */
  ovVariant?: PaginationVariant;
}

export const DEFAULT_PAGE_SIZES = [15, 25, 50, 100];

const normalizePaginationTotal = (total?: number): number => {
  if (typeof total !== 'number' || !Number.isFinite(total) || total < 0) {
    return 0;
  }
  return total;
};

const paginationRenderItem = (
  item: Parameters<NonNullable<PaginationProps['renderItem']>>[0],
) => (
  <PaginationItem
    {...item}
    data-testid={
      item.type === 'page'
        ? `pagination-button-${item.page}`
        : `pagination-${item.type}-button`
    }
  />
);

export const Pagination = ({
  perPage = 15,
  onChangePerPage,
  total,
  showPerPage = true,
  pageSizes = DEFAULT_PAGE_SIZES,
  summaryKey = 'paginationSummary',
  summaryNamespace,
  ovVariant = 'default',
  ...props
}: IPaginationProps) => {
  const summaryNs = summaryNamespace ?? 'shared';
  const i18nNamespaces =
    summaryNs !== 'shared' ? [summaryNs, 'shared'] : ['shared'];
  const { t } = useTranslation(i18nNamespaces);
  const { t: tShared } = useTranslation('shared');

  const page = props.page || 1;
  const offSet = perPage * (page - 1);
  const safeTotal = normalizePaginationTotal(total);
  const showSummary = safeTotal > 0;
  const summaryStart = showSummary ? offSet + 1 : 0;
  const summaryEnd = showSummary ? Math.min(offSet + perPage, safeTotal) : 0;
  const summaryLabel = t(summaryKey, {
    start: summaryStart,
    end: summaryEnd,
    total: safeTotal,
  });

  const paginationSx = usePaginationSx(props.sx, ovVariant);

  const perPageSelect =
    showPerPage &&
    perPage &&
    onChangePerPage &&
    (ovVariant === 'tableFooter' ? (
      <TableFooterPerPageSelect
        perPage={perPage}
        pageSizes={pageSizes}
        onChangePerPage={onChangePerPage}
        disabled={props.disabled}
      />
    ) : (
      <SelectField
        testId='pagination-per-page-select'
        label=''
        value={perPage}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChangePerPage(Number(e.target.value))
        }
        size='small'
        sx={{ p: 0 }}
        disabled={props.disabled}
      >
        {pageSizes.map((size) => (
          <MenuItem
            key={size}
            testId={`pagination-per-page-select-${size}-menu-item`}
            value={size}
          >
            {tShared('perPageWithSize', { size })}
          </MenuItem>
        ))}
      </SelectField>
    ));

  if (ovVariant === 'default') {
    return (
      <Box
        display='flex'
        justifyContent={showSummary ? 'space-between' : 'end'}
        alignItems='center'
        overflow='hidden'
      >
        {showSummary && (
          <Typography variant='bodySmall'>{summaryLabel}</Typography>
        )}
        <Box display='flex' alignItems='center' overflow='hidden'>
          {perPageSelect}
          <MuiPagination
            {...props}
            sx={paginationSx}
            data-testid='pagination'
            renderItem={paginationRenderItem}
          />
        </Box>
      </Box>
    );
  }

  return (
    <TableFooterPaginationLayout
      showSummary={showSummary}
      summaryLabel={summaryLabel}
      perPageSelect={perPageSelect}
      paginationProps={props}
      paginationSx={paginationSx}
    />
  );
};

type TableFooterPerPageSelectProps = {
  perPage: number;
  pageSizes: number[];
  onChangePerPage: (perPage: number) => void;
  disabled?: boolean;
};

const TableFooterPerPageSelect = ({
  perPage,
  pageSizes,
  onChangePerPage,
  disabled,
}: TableFooterPerPageSelectProps) => {
  const { t } = useTranslation('shared');
  const { sys, comp } = useThemeTokens(usePaginationTokens());
  const paginationTokens = comp.pagination;

  const outline = sys.color.outlineVariant ?? sys.color.outline;
  const surface = sys.color.surface ?? '#fff';
  const muted = sys.color.onSurfaceVariant ?? sys.color.onSurface;

  const perPageSx = {
    p: 0,
    minWidth: 72,
    ...(paginationTokens.perPageSelect && {
      '& .MuiOutlinedInput-root': {
        borderRadius: paginationTokens.perPageSelect.borderRadius
          ? `${paginationTokens.perPageSelect.borderRadius} !important`
          : '6px !important',
        backgroundColor: paginationTokens.perPageSelect.backgroundColor
          ? `${paginationTokens.perPageSelect.backgroundColor} !important`
          : `${surface} !important`,
        '& fieldset': {
          borderColor: paginationTokens.perPageSelect.borderColor ?? outline,
          borderWidth: paginationTokens.perPageSelect.borderWidth ?? '1px',
        },
      },
      '& .MuiSelect-select': {
        color: paginationTokens.perPageSelect.color
          ? `${paginationTokens.perPageSelect.color} !important`
          : `${muted} !important`,
        fontSize: paginationTokens.perPageSelect.fontSize
          ? `${paginationTokens.perPageSelect.fontSize} !important`
          : '14px !important',
        fontWeight: paginationTokens.perPageSelect.fontWeight
          ? `${paginationTokens.perPageSelect.fontWeight} !important`
          : '500 !important',
        py: '6px',
      },
      '& .MuiSelect-icon': {
        color: paginationTokens.perPageSelect.color ?? muted,
      },
    }),
  };

  return (
    <Box display='flex' alignItems='center' gap={1}>
      <Typography
        variant='bodySmall'
        weight='regular'
        sx={{ color: muted, whiteSpace: 'nowrap' }}
      >
        {t('rowsPerPage')}
      </Typography>
      <SelectField
        testId='pagination-per-page-select'
        label=''
        value={perPage}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChangePerPage(Number(e.target.value))
        }
        size='small'
        sx={perPageSx}
        disabled={disabled}
        renderValue={(selected: unknown) => String(selected)}
      >
        {pageSizes.map((size) => (
          <MenuItem
            key={size}
            testId={`pagination-per-page-select-${size}-menu-item`}
            value={size}
          >
            {String(size)}
          </MenuItem>
        ))}
      </SelectField>
    </Box>
  );
};

type TableFooterPaginationLayoutProps = {
  showSummary: boolean;
  summaryLabel: string;
  perPageSelect: ReactNode;
  paginationProps: PaginationProps;
  paginationSx: SxProps<Theme>;
};

const TableFooterPaginationLayout = ({
  showSummary,
  summaryLabel,
  perPageSelect,
  paginationProps,
  paginationSx,
}: TableFooterPaginationLayoutProps) => {
  const { sys } = useThemeTokens();
  const surface = sys.color.surface ?? '#fff';
  const barBg =
    sys.color.surfaceContainer ?? sys.color.surfaceContainerVariant ?? surface;
  const muted = sys.color.onSurfaceVariant ?? sys.color.onSurface;

  return (
    <Box
      display='flex'
      justifyContent={showSummary ? 'space-between' : 'end'}
      alignItems='center'
      flexWrap='wrap'
      rowGap={1}
      columnGap={2}
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: 2,
        py: 1.5,
        backgroundColor: barBg,
      }}
    >
      {showSummary && (
        <Typography variant='bodySmall' weight='regular' sx={{ color: muted }}>
          {summaryLabel}
        </Typography>
      )}
      <Box
        display='flex'
        alignItems='center'
        flexWrap='wrap'
        gap={2}
        sx={{ ml: showSummary ? undefined : 'auto' }}
      >
        {perPageSelect}
        <MuiPagination
          {...paginationProps}
          sx={paginationSx}
          data-testid='pagination'
          showFirstButton
          showLastButton
          shape='rounded'
          renderItem={paginationRenderItem}
        />
      </Box>
    </Box>
  );
};

export const TablePagination = (props: TablePaginationProps) => {
  const { t } = useTranslation('shared');
  return (
    <MuiTablePagination
      labelRowsPerPage={t('rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        t('paginationSummary', {
          start: from,
          end: to,
          total: count !== -1 ? count : `${to}+`,
        })
      }
      {...props}
    />
  );
};
